<template>
  <div class="base-checkbox">
    <Field v-slot="{ errors, resetField }" :name="name || ''">
      <label
        :class="{
          disabled: disabled,
          'slide-toggle': slideToggle,
        }"
      >
        <input
          :key="inputReactivityKey"
          ref="checkbox"
          v-model="model"
          type="checkbox"
          v-bind="$attrs"
          :disabled="disabled"
          :name="name"
          @change="resetField()"
        />
        <slot name="checkboxVisual">
          <span :class="[slideToggle ? 'checkbox-slider' : 'checkbox-tick']" />
        </slot>
        <span v-if="$slots.image" class="checkbox-image">
          <slot name="image" />
        </span>
        <slot />
      </label>
      <p v-if="$slots.helpText" class="help-text">
        <slot name="helpText" />
      </p>
      <div class="error" v-if="errors.length">
        <transition name="fade">
          <span class="input-error-msg">{{ errors[0] }}</span>
        </transition>
      </div>
    </Field>
  </div>
</template>

<script lang="ts" setup>
import { Field } from 'vee-validate'

const props = defineProps<{
  modelValue: boolean
  slideToggle?: boolean
  name?: string
  disabled?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'change', value: boolean): void
}>()

const model = computed({
  get() {
    return props.modelValue
  },
  set(value) {
    requestIdleCallback(() => {
      emit('update:modelValue', value)
      emit('change', value)
    })
  },
})

const inputReactivityKey = computed(() => String(model.value) + Date.now())
</script>

<style scoped>
label {
  align-items: center;
  cursor: pointer;
  display: flex !important;
  flex-direction: row;
  font-size: var(--body);
  font-weight: normal;
  justify-content: flex-start;
  margin-bottom: 0.5em;
  position: relative;
}

input {
  position: absolute;
  opacity: 0;
  z-index: -1;
}

.checkbox-tick {
  border-radius: 0.2em;
  height: 1.6em;
  width: 1.6em;
  flex-shrink: 0;
  background-color: #eee;
  border: 1px solid var(--colour-light-grey);
  margin-right: 0.5em;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
}

.sml .checkbox-tick {
  font-size: 0.75em;
}

.checkbox-tick:after {
  content: '';
  position: absolute;
  display: none;
  visibility: hidden;
  width: 0.6em;
  height: 1em;
  border: solid white;
  border-width: 0 0.2em 0.2em 0;
  transform: translateY(-0.1em) rotate(45deg);
}

input:checked ~ .checkbox-tick:after {
  display: block;
  visibility: visible;
}

input:checked ~ .checkbox-tick {
  background-color: var(--colour-header-dark-blue);
  border-color: var(--colour-header-dark-blue);
}

label.disabled {
  color: #bbb;
  pointer-events: none;
  cursor: not-allowed;
}

input:disabled ~ .checkbox-tick {
  background-color: #eee;
  border-color: #ddd;
  cursor: not-allowed;
}

input:disabled ~ .checkbox-tick:after {
  border-color: var(--colour-table-borders);
}

.checkbox-image {
  display: block;
  width: 2.5em;
  height: 2.5em;
  margin-right: 0.5em;
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
}

.slide-toggle {
  flex-direction: row-reverse;
  gap: 0.5em;
  margin: 0;
}

.slide-toggle .checkbox-slider {
  cursor: pointer;
  position: relative;
  background-color: #d0d5e1;
  transition: transform 0.4s;
  border-radius: 2em;
  width: 2.5em;
  height: 1.5em;
  flex-shrink: 0;
}

.sml .checkbox-slider {
  font-size: 0.75em;
}

.slide-toggle .checkbox-slider:before {
  position: absolute;
  content: '';
  height: calc(1.5em - 2px);
  width: calc(1.5em - 2px);
  left: 1px;
  bottom: 1px;
  border-radius: 50%;
  background-color: white;
  transition: transform 0.4s;
}

.slide-toggle input:checked + .checkbox-slider {
  background-color: var(--colour-light-green);
}

.slide-toggle input:checked + .checkbox-slider:before {
  -webkit-transform: translateX(1em);
  -ms-transform: translateX(1em);
  transform: translateX(1em);
}

input:disabled ~ .checkbox-slider {
  background-color: var(--colour-page-background);
  cursor: not-allowed;
}

input:disabled ~ .checkbox-slider:before {
  background-color: white;
}

.base-checkbox .input-error-msg:after {
  left: 0;
  right: auto;
}

@media (hover: hover) {
  label:hover input ~ .checkbox-tick {
    background-color: var(--colour-page-background);
    border-color: var(--colour-header-dark-blue);
    box-shadow: 0 0 0 2px var(--colour-header-dark-blue);
  }

  label:hover input:not([disabled]):checked ~ .checkbox-tick {
    background-color: var(--colour-dark-blue);
    border-color: var(--colour-dark-blue);
    box-shadow: none;
  }

  label:hover input:not([disabled]):checked ~ .checkbox-slider {
    background-color: var(--colour-header-light-blue);
  }

  label:hover input ~ .checkbox-slider {
    background-color: var(--colour-header-light-blue);
  }
}
</style>
